import { useEffect, useState } from "react";
import { useLang } from "../context/LanguageContext";
import SubmissionsPanel from "../components/SubmissionsPanel";
import CarsAdmin from "../components/CarsAdmin";
import ResultsAdmin from "../components/ResultsAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const { t } = useLang();
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState(null);
  const [tab, setTab] = useState("leads");

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => setUser(u))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) {
      fetch(`${API}/leads`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then(setLeads)
        .catch(() => setLeads([]));
    }
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) throw new Error();
      setUser(await res.json());
    } catch {
      setError(t("admin.error"));
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
  };

  if (user === undefined) return <div className="min-h-screen bg-[#050607]" data-testid="admin-loading" />;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050607] flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-[#090A0B] p-8 md:p-10">
          <div className="wordmark text-[15px] text-[#F3F3F0] mb-10">{t("admin.login")}</div>
          <label className="field-label" htmlFor="admin-email">{t("admin.email")}</label>
          <input id="admin-email" type="email" required className="field-input mb-6" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
          <label className="field-label" htmlFor="admin-password">{t("admin.password")}</label>
          <input id="admin-password" type="password" required className="field-input mb-8" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
          {error && <p className="mono text-[11px] text-[#D1C7BD] mb-5 uppercase tracking-[0.14em]" data-testid="admin-error">{error}</p>}
          <button type="submit" className="btn btn-solid w-full justify-between" data-testid="admin-login-btn">
            <span>{t("admin.enter")}</span><span aria-hidden="true">→</span>
          </button>
        </form>
      </div>
    );
  }

  const nameOf = (l) => l.company_name || l.full_name || "—";

  const patchLead = async (id, status) => {
    const res = await fetch(`${API}/leads/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      const updated = await res.json();
      setLeads((list) => list.map((x) => (x.id === id ? updated : x)));
    }
  };

  const exportCsv = (filename, items) => {
    if (!items || items.length === 0) return;
    const keys = [...new Set(items.flatMap((o) => Object.keys(o)))];
    const cell = (v) => '"' + String(v ?? "").replace(/"/g, '""') + '"';
    const rows = [keys.join(",")].concat(items.map((o) => keys.map((k) => {
      const val = o[k];
      const out = Array.isArray(val) ? val.map((x) => (typeof x === "object" ? JSON.stringify(x) : x)).join("; ") : val;
      return cell(out);
    }).join(",")));
    const blob = new Blob(["\ufeff" + rows.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div className="min-h-screen bg-[#050607] px-5 md:px-10 py-12" data-testid="admin-leads">
      <div className="flex items-center justify-between mb-10">
        <div className="wordmark text-[15px] text-[#F3F3F0]">{t("admin.login")}</div>
        <button onClick={logout} className="btn !py-2 !px-4" data-testid="admin-logout-btn">{t("admin.logout")}</button>
      </div>
      <div className="flex flex-wrap gap-8 mb-8 border-b border-white/10" data-testid="admin-tabs">
        <button onClick={() => setTab("leads")} data-testid="admin-tab-leads"
          className={`mono text-[11px] uppercase tracking-[0.14em] pb-3 -mb-px transition-colors duration-300 ${tab === "leads" ? "text-[#F3F3F0] border-b border-[#F3F3F0]" : "text-[#666663] hover:text-[#999995] border-b border-transparent"}`}>
          {t("admin.leads")}
        </button>
        <button onClick={() => setTab("submissions")} data-testid="admin-tab-submissions"
          className={`mono text-[11px] uppercase tracking-[0.14em] pb-3 -mb-px transition-colors duration-300 ${tab === "submissions" ? "text-[#F3F3F0] border-b border-[#F3F3F0]" : "text-[#666663] hover:text-[#999995] border-b border-transparent"}`}>
          {t("sell.admin.tab")}
        </button>
        <button onClick={() => setTab("cars")} data-testid="admin-tab-cars"
          className={`mono text-[11px] uppercase tracking-[0.14em] pb-3 -mb-px transition-colors duration-300 ${tab === "cars" ? "text-[#F3F3F0] border-b border-[#F3F3F0]" : "text-[#666663] hover:text-[#999995] border-b border-transparent"}`}>
          {t("nx.cars.adminTab")}
        </button>
        <button onClick={() => setTab("results")} data-testid="admin-tab-results"
          className={`mono text-[11px] uppercase tracking-[0.14em] pb-3 -mb-px transition-colors duration-300 ${tab === "results" ? "text-[#F3F3F0] border-b border-[#F3F3F0]" : "text-[#666663] hover:text-[#999995] border-b border-transparent"}`}>
          {t("nx.results.adminTab")}
        </button>
      </div>
      {tab === "submissions" && <SubmissionsPanel onExport={exportCsv} />}
      {tab === "cars" && <CarsAdmin />}
      {tab === "results" && <ResultsAdmin />}
      {tab === "leads" && (
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="eyebrow !mb-0">{t("admin.leads")}{leads ? ` — ${leads.length}` : ""}</div>
          {leads && leads.length > 0 && (
            <button onClick={() => exportCsv(`veyrone-leads-${new Date().toISOString().slice(0, 10)}.csv`, leads)}
              className="btn !py-2 !px-4" data-testid="admin-leads-export-btn">{t("sell.admin.exportCsv")}</button>
          )}
        </div>
      )}
      {tab === "leads" && leads === null && <p className="mono text-[12px] text-[#666663]">…</p>}
      {tab === "leads" && leads && leads.length === 0 && <p className="mono text-[12px] text-[#666663]" data-testid="admin-leads-empty">{t("admin.empty")}</p>}
      {tab === "leads" && leads && leads.length > 0 && (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-left" data-testid="admin-leads-table">
            <thead>
              <tr className="border-b border-white/10">
                {[t("admin.colDate"), t("admin.colType"), t("admin.colName"), t("admin.colEmail"), t("admin.colCountry"), t("sell.admin.colStatus"), t("admin.colDetails")].map((h) => (
                  <th key={h} className="px-4 py-3">
                    <span className="field-label !text-[9px] !mb-0">{h}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-white/[0.06] align-top hover:bg-white/[0.02] transition-colors" data-testid="admin-lead-row">
                  <td className="px-4 py-3 mono text-[11px] text-[#999995] whitespace-nowrap">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 mono text-[11px] text-[#D1C7BD] uppercase">{l.type}</td>
                  <td className="px-4 py-3 text-[13px] text-[#F3F3F0]">{nameOf(l)}</td>
                  <td className="px-4 py-3 mono text-[11px] text-[#999995]">{l.email}</td>
                  <td className="px-4 py-3 mono text-[11px] text-[#999995]">{l.country || "—"}</td>
                  <td className="px-4 py-3">
                    <select className="field-input !py-1.5 !text-[11px] cursor-pointer min-w-[130px]" value={l.status || "new"}
                      onChange={(e) => patchLead(l.id, e.target.value)} data-testid={`lead-status-select-${l.id}`}>
                      {["new", "under_review", "contacted", "offer_made", "purchased", "rejected"].map((st) => (
                        <option key={st} value={st}>{t(`sell.admin.statuses.${st}`)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <details>
                      <summary className="mono text-[10px] text-[#666663] cursor-pointer hover:text-[#F3F3F0] uppercase tracking-[0.14em]">{t("admin.colDetails")}</summary>
                      <div className="mt-3 space-y-1 max-w-md">
                        {Object.entries(l).filter(([k]) => !["id", "created_at", "type", "email", "country", "company_name", "full_name"].includes(k)).map(([k, v]) => (
                          <div key={k} className="flex gap-3">
                            <span className="mono text-[10px] text-[#555654] uppercase min-w-[130px]">{k.replace(/_/g, " ")}</span>
                            <span className="text-[12px] text-[#999995]">{String(v) || "—"}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
